<div class="card shadow-sm mb-3">
  <div class="card-body">

    <div *ngIf="tocLoadStatus" class="alert alert-info py-2 small mb-3 d-flex align-items-center gap-2" role="status">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      <span>{{ tocLoadStatus }}</span>
    </div>

    <h4>Table of Contents</h4>

    <div class="alert alert-secondary py-2 small mb-3" *ngIf="needsTocGeneration && !tocLoadStatus">
      <p class="mb-2">This lesson has no table of contents yet. Generate one from lesson properties.</p>
      <button type="button" class="btn btn-success btn-sm" (click)="generateToc()" [disabled]="actionBusy">
        Generate TOC
      </button>
    </div>

    <div class="d-flex flex-wrap gap-2 mb-3" *ngIf="toc">
      <button
        type="button"
        class="btn btn-outline-warning btn-sm"
        *ngIf="canClearLessonContent()"
        (click)="clearLessonContent()"
        [disabled]="actionBusy || !!tocLoadStatus"
      >
        Clear and Recreate TOC
      </button>
      <button
        type="button"
        class="btn btn-outline-danger btn-sm"
        *ngIf="!linkedToProject"
        (click)="deleteLesson()"
        [disabled]="actionBusy || !!tocLoadStatus"
      >
        Delete lesson
      </button>
    </div>
    <p class="text-muted small mb-3">
      Use the <strong>Slide type</strong> column to pick the layout shortcode for each slide (e.g. BLCC, IMAGEBLCC). This is saved with the TOC. You can still change type on the full slide editor via <strong>Edit Slide</strong>.
      <span *ngIf="toc && isDocumentStrictLesson(toc.lessonContentMode)" class="d-block mt-1">
        <strong>Document-specific lesson:</strong> set <strong>PDF pages</strong> per slide (1-based index in the uploaded PDF, e.g. 12 or 12-14). Images are cropped from those PDF pages.
      </span>
    </p>

    <!-- TOC Opening Note -->
    <div class="mb-3" *ngIf="toc">
      <label class="form-label">Lesson opening note</label>
      <textarea class="form-control" rows="2" [(ngModel)]="toc.openingNote" placeholder="Opening note for the lesson"></textarea>
    </div>

    <!-- Background Image -->
    <div class="card mb-3">
      <div class="card-body">

        <h6>TOC Background Image</h6>

        <input type="file" class="form-control mb-2" accept="image/png,image/jpeg"
          (change)="onBackgroundSelected($event)">

        <div *ngIf="backgroundError" class="text-danger mb-2">
          {{ backgroundError }}
        </div>

        <!-- Preview -->
        <div *ngIf="backgroundPreviewUrl" class="mb-2">
          <img [src]="backgroundPreviewUrl" class="img-fluid rounded border" style="max-height: 200px;">
        </div>

        <!-- Existing image -->
        <div *ngIf="!backgroundPreviewUrl && backgroundImageName">
          <img [src]="getBackgroundUrl()" class="img-fluid rounded border" style="max-height: 200px;">
        </div>

        <div *ngIf="backgroundImageName" class="mt-2">
          <button class="btn btn-sm btn-outline-danger" (click)="removeBackground()">
            Delete Background
          </button>
        </div>

      </div>
    </div>

    <!-- Sections -->
    <div *ngIf="!toc" class="text-muted small">No TOC loaded.</div>
    <ng-container *ngIf="toc">
    <div *ngFor="let section of toc.sections; let si = index" class="border rounded p-3 mb-3">

      <div class="d-flex justify-content-between align-items-center">
        <label class="form-label">Section:   </label>
        <input class="form-control me-2" placeholder="Section title" [(ngModel)]="section.title">

        <div>
          <button class="btn btn-sm btn-outline-secondary" (click)="moveSectionUp(si)">↑</button>
          <button class="btn btn-sm btn-outline-secondary" (click)="moveSectionDown(si)">↓</button>
          <button class="btn btn-sm btn-outline-danger" (click)="removeSection(si)">🗑</button>
        </div>
      </div>

      <textarea class="form-control mt-2" rows="2" placeholder="Section description"
        [(ngModel)]="section.description"></textarea>

      <!-- Slides -->
      <table class="table table-sm mt-3">
        <thead>
          <tr>
            <th>Slide</th>
            <th>Slide type</th>
            <th *ngIf="isDocumentStrictLesson(toc.lessonContentMode)">PDF pages</th>
            <th>Description</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="let slide of section.slides; let i = index"
            [attr.id]="tocSlideDomId(slide.slideId)"
            [class.table-active]="highlightedSlideId === slide.slideId"
          >
            <td>
              <input class="form-control" [(ngModel)]="slide.title">
            </td>

            <td style="min-width: 14rem;">
              <select class="form-select form-select-sm" [(ngModel)]="slide.slideType">
                <option value="">— Select type —</option>
                <option *ngFor="let st of slideTypeOptions" [value]="st.code">
                  {{ st.code }} — {{ st.label }}
                </option>
              </select>
            </td>

            <td *ngIf="isDocumentStrictLesson(toc.lessonContentMode)" style="min-width: 8rem;">
              <span *ngIf="isQuizSlide(slide)" class="text-muted small">—</span>
              <input
                *ngIf="!isQuizSlide(slide)"
                class="form-control form-control-sm"
                type="text"
                [(ngModel)]="slide.sourcePageRef"
                placeholder="e.g. 12 or 12-14"
                title="1-based page number(s) in the uploaded PDF file"
                [class.is-invalid]="isDocumentStrictLesson(toc.lessonContentMode) && !slide.sourcePageRef?.trim() && slide.status !== 'FILLED'"
              />
            </td>

            <td>
              <input class="form-control" [(ngModel)]="slide.description">
            </td>

            <td>
              <span class="badge bg-secondary">
                {{ slide.status }}
              </span>
            </td>

            <td>
              <button class="btn btn-sm btn-outline-secondary" (click)="moveSlideUp(section, i)">↑</button>
              <button class="btn btn-sm btn-outline-secondary" (click)="moveSlideDown(section, i)">↓</button>
              <button class="btn btn-sm btn-outline-danger" (click)="removeSlide(section, i)">🗑</button>
              <button class="btn btn-sm btn-outline-danger" (click)="EditSlide(section, i)" [disabled]="tocGenerating">Edit Slide</button>
            </td>
          </tr>
        </tbody>
      </table>

      <button class="btn btn-sm btn-outline-primary" (click)="addSlide(section)">
        + Add Slide
      </button>
    </div>

    <button class="btn btn-outline-primary" (click)="addSection()">
      + Add Section
    </button>
    </ng-container>

    <hr>
    <!-- TOC Closing Note -->
    <div class="mb-3" *ngIf="toc">
      <label class="form-label">Lesson closing note</label>
      <textarea class="form-control" rows="2" [(ngModel)]="toc.closingNote" placeholder="Closing note for the lesson"></textarea>
    </div>

    <!-- Actions -->
    <div class="d-flex justify-content-between">
      <button class="btn btn-outline-secondary" (click)="goback()">
        Go back to lessons list
      </button>
      <button class="btn btn-primary" (click)="saveTOC()" [disabled]="tocGenerating || actionBusy">
        {{ tocGenerating ? 'Generating…' : 'Save' }}
      </button>

      <div>
        <button class="btn btn-success me-2" type="button" (click)="generatePPT()" [disabled]="pptGenBusy || tocGenerating">
          {{ pptGenBusy ? 'Generating…' : 'Generate PPT' }}
        </button>

        <select class="form-select d-inline w-auto me-2" [(ngModel)]="selectedLanguage">
          <option value="">Translate & Generate</option>
          <option *ngFor="let lang of translateLanguages" [value]="lang">
            {{ lang }}
          </option>
        </select>

        <button class="btn btn-warning" [disabled]="!selectedLanguage" (click)="translateAndGenerate()">
          Go
        </button>
      </div>
    </div>

  </div>
</div>